import React, { useState, useEffect } from 'react';

interface DateFiltersProps {
  onFiltersChange: (filters: any) => void;
  loading?: boolean;
  propertiesCount?: number;
  totalProperties?: number;
}

interface DateRange {
  inicio?: string;
  fin?: string;
}

export const DateFilters: React.FC<DateFiltersProps> = ({ 
  onFiltersChange, 
  loading = false,
  propertiesCount = 0,
  totalProperties = 0
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<string>('all');
  const [customRange, setCustomRange] = useState<DateRange>({});
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [showModifiedOnly, setShowModifiedOnly] = useState(false);

  // Períodos predefinidos específicos para inmobiliaria
  const periods = [
    { 
      id: 'all', 
      label: 'Todo el tiempo', 
      icon: '🏠',
      description: 'Todas las propiedades'
    },
    { 
      id: 'today', 
      label: 'Hoy', 
      icon: '📅',
      description: 'Propiedades captadas hoy'
    },
    { 
      id: 'yesterday', 
      label: 'Ayer', 
      icon: '⏰',
      description: 'Propiedades captadas ayer'
    },
    { 
      id: 'week', 
      label: 'Esta semana', 
      icon: '📊',
      description: 'Últimos 7 días'
    },
    { 
      id: 'month', 
      label: 'Este mes', 
      icon: '📈',
      description: 'Desde inicio de mes'
    },
    { 
      id: 'last30', 
      label: 'Últimos 30 días', 
      icon: '📋',
      description: 'Últimos 30 días'
    },
    { 
      id: 'custom', 
      label: 'Rango personalizado', 
      icon: '🎯',
      description: 'Seleccionar fechas específicas'
    }
  ];

  // Aplicar filtros cuando cambia el período seleccionado
  useEffect(() => {
    const applyFilters = () => {
      let filters: any = {};
      
      switch (selectedPeriod) {
        case 'today':
          const today = new Date().toISOString().split('T')[0];
          filters = { fecha_creacion_inicio: today, fecha_creacion_fin: today };
          break;
          
        case 'yesterday':
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          const yesterdayStr = yesterday.toISOString().split('T')[0];
          filters = { fecha_creacion_inicio: yesterdayStr, fecha_creacion_fin: yesterdayStr };
          break;
          
        case 'week':
          const weekStart = getWeekStart();
          const weekEnd = new Date().toISOString().split('T')[0];
          filters = { fecha_creacion_inicio: weekStart, fecha_creacion_fin: weekEnd };
          break;
          
        case 'month':
          const monthStart = getMonthStart();
          const monthEnd = new Date().toISOString().split('T')[0];
          filters = { fecha_creacion_inicio: monthStart, fecha_creacion_fin: monthEnd };
          break;

        case 'last30':
          const last30Start = new Date();
          last30Start.setDate(last30Start.getDate() - 30);
          const last30End = new Date().toISOString().split('T')[0];
          filters = { 
            fecha_creacion_inicio: last30Start.toISOString().split('T')[0], 
            fecha_creacion_fin: last30End 
          };
          break;
          
        case 'custom':
          if (customRange.inicio && customRange.fin) {
            filters = { 
              fecha_creacion_inicio: customRange.inicio, 
              fecha_creacion_fin: customRange.fin 
            };
          }
          break;
          
        default:
          filters = {};
      }

      // Agregar filtro de solo modificadas si está activo
      if (showModifiedOnly) {
        filters.solo_modificadas = true;
      }
      
      onFiltersChange(filters);
    };

    applyFilters();
  }, [selectedPeriod, customRange, showModifiedOnly, onFiltersChange]);

  // Obtener inicio de semana (lunes)
  const getWeekStart = () => {
    const date = new Date();
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(date.setDate(diff));
    return monday.toISOString().split('T')[0];
  };

  // Obtener inicio de mes
  const getMonthStart = () => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0];
  };

  // Manejar cambio de rango personalizado
  const handleCustomRangeChange = (field: 'inicio' | 'fin', value: string) => {
    setCustomRange(prev => ({ ...prev, [field]: value }));
  };

  // Limpiar filtros
  const clearFilters = () => {
    setSelectedPeriod('all');
    setCustomRange({});
    setShowCustomRange(false);
    setShowModifiedOnly(false);
    onFiltersChange({});
  };

  // Obtener label para el período seleccionado
  const getSelectedPeriodLabel = () => {
    const period = periods.find(p => p.id === selectedPeriod);
    return period ? period.label : 'Filtro personalizado';
  };

  // Obtener estadísticas rápidas
  const getQuickStats = () => {
    if (selectedPeriod === 'all') {
      return `${totalProperties} propiedades totales`;
    }
    
    const percentage = totalProperties > 0 ? Math.round((propertiesCount / totalProperties) * 100) : 0;
    return `${propertiesCount} propiedades (${percentage}% del total)`;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Filtros por Fechas
        </h3>
        
        <div className="flex items-center space-x-3">
          {/* Estadísticas rápidas */}
          <div className="text-sm text-gray-600">
            {getQuickStats()}
          </div>
          
          {selectedPeriod !== 'all' && (
            <button
              onClick={clearFilters}
              className="text-sm text-gray-500 hover:text-red-600 flex items-center gap-1 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Limpiar
            </button>
          )}
        </div>
      </div>

      {/* Filtro de propiedades modificadas */}
      <div className="mb-4">
        <label className="flex items-center space-x-2 text-sm">
          <input
            type="checkbox"
            checked={showModifiedOnly}
            onChange={(e) => setShowModifiedOnly(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            disabled={loading}
          />
          <span className="text-gray-700">Solo propiedades modificadas recientemente</span>
          {showModifiedOnly && (
            <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full text-xs">
              Activo
            </span>
          )}
        </label>
      </div>

      {/* Botones de períodos predefinidos */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
        {periods.map((period) => {
          const isActive = selectedPeriod === period.id;
          
          return (
            <button
              key={period.id}
              onClick={() => {
                setSelectedPeriod(period.id);
                setShowCustomRange(period.id === 'custom');
              }}
              disabled={loading}
              className={`
                relative flex flex-col items-center justify-center gap-1 px-3 py-3 rounded-lg text-sm font-medium transition-all min-h-[80px]
                ${isActive 
                  ? 'bg-blue-600 text-white shadow-lg transform scale-105' 
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200 hover:border-gray-300'
                }
                ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
              title={period.description}
            >
              <span className="text-xl">{period.icon}</span>
              <span className="text-center leading-tight">{period.label}</span>
              {isActive && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full"></div>
              )}
            </button>
          );
        })}
      </div>

      {/* Rango personalizado */}
      {showCustomRange && (
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h4 className="text-sm font-medium text-gray-700">Rango personalizado</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Fecha de inicio
              </label>
              <input
                type="date"
                value={customRange.inicio || ''}
                onChange={(e) => handleCustomRangeChange('inicio', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Fecha de fin
              </label>
              <input
                type="date"
                value={customRange.fin || ''}
                onChange={(e) => handleCustomRangeChange('fin', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              />
            </div>
          </div>
          
          {/* Validación del rango */}
          {customRange.inicio && customRange.fin && (
            <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
              <div className="flex items-center gap-1 text-blue-800">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>
                  Rango válido: {customRange.inicio} a {customRange.fin}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Indicador de filtros activos */}
      {selectedPeriod !== 'all' && (
        <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-blue-800">
              Filtro activo: {getSelectedPeriodLabel()}
            </span>
            {selectedPeriod === 'custom' && customRange.inicio && customRange.fin && (
              <span className="text-sm text-blue-600">
                ({customRange.inicio} - {customRange.fin})
              </span>
            )}
            {showModifiedOnly && (
              <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full text-xs">
                + Solo modificadas
              </span>
            )}
          </div>
          
          <div className="text-sm text-blue-600">
            {propertiesCount} propiedades encontradas
          </div>
        </div>
      )}

      {/* Tips para el equipo */}
      <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-start gap-2">
          <svg className="w-4 h-4 text-gray-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="text-sm text-gray-600">
            <p className="font-medium mb-1">💡 Tips para el equipo:</p>
            <ul className="text-xs space-y-1">
              <li>• Usa "Hoy" para ver propiedades captadas hoy</li>
              <li>• "Esta semana" muestra el progreso semanal</li>
              <li>• "Últimos 30 días" es ideal para reportes mensuales</li>
              <li>• Activa "Solo modificadas" para ver actualizaciones recientes</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};